import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Volume2, VolumeX } from 'lucide-react';
import heroImg from '../assets/011v2.jpg';

const VIDEOS = [
  '/LSPA_teaser.mp4',      // Video 1 (original German)
  '/LSPA_teaser_en.mp4',   // Video 2 (English)
];

export const HeroSection = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [currentVideoIdx, setCurrentVideoIdx] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [allEnded, setAllEnded] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (muted) {
      video.volume = 1.0;
      video.muted = false;
      if (video.paused || video.ended) {
        video.play().catch(() => {});
      }
      setMuted(false);
    } else {
      video.muted = true;
      setMuted(true);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay    = () => setVideoPlaying(true);
    const onPause   = () => setVideoPlaying(false);
    const onLoaded  = () => { setVideoLoaded(true); if (!video.paused) setVideoPlaying(true); };
    const onError   = () => setVideoLoaded(true); // show fallback image on error

    const onEnded = () => {
      const nextIdx = currentVideoIdx + 1;
      if (nextIdx < VIDEOS.length) {
        // Play next video
        setCurrentVideoIdx(nextIdx);
        setVideoLoaded(false);
        video.src = VIDEOS[nextIdx];
        video.load();
        video.play().catch(() => {});
      } else {
        // All videos done → show hotel image
        setAllEnded(true);
        setVideoPlaying(false);
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('canplay', onLoaded);
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onError);

    if (video.readyState >= 2) setVideoLoaded(true);
    const timer = setTimeout(() => setVideoLoaded(true), 2000);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('canplay', onLoaded);
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
      clearTimeout(timer);
    };
  }, [currentVideoIdx]);

  const replay = () => {
    const video = videoRef.current;
    if (!video) return;
    // Restart from video 1
    setCurrentVideoIdx(0);
    setAllEnded(false);
    setVideoLoaded(false);
    video.src = VIDEOS[0];
    video.muted = true;
    video.load();
    video.play().catch(() => {});
  };

  // Text visible only when both videos ended OR before first video loads
  const textVisible = allEnded || !videoPlaying;

  return (
    <section className="hero" id="hero" data-testid="hero-section">
      {/* Static hotel image — always as base */}
      <div className="hero-bg">
        <img src={heroImg} alt="Legend Spa Hotel" />
      </div>

      {/* Video overlay — fades out when all done */}
      <div
        className="hero-video"
        style={{ opacity: allEnded ? 0 : 1, transition: 'opacity 1.5s ease' }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          loop={false}
          preload="auto"
          style={{
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 1.5s ease',
          }}
          data-testid="hero-video"
        >
          <source src={VIDEOS[0]} type="video/mp4" />
        </video>
      </div>

      <div className="hero-overlay" />

      {/* Hero text — hidden while video plays */}
      <div
        className="hero-content"
        style={{
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 1s ease',
          pointerEvents: textVisible ? 'all' : 'none',
        }}
      >
        <p className="hero-tagline">{t('hero.tagline')}</p>
        <h1 className="hero-title">{t('hero.title')}</h1>
        <p className="hero-slogan">{t('hero.slogan')}</p>
        <p className="hero-subtitle">{t('hero.subtitle')}</p>
      </div>

      {/* Replay button when all videos ended */}
      {allEnded && (
        <div className="hero-replay-overlay">
          <button
            className="hero-replay-btn"
            onClick={replay}
            data-testid="hero-replay-button"
          >
            <Play size={20} fill="currentColor" />
            {t('hero.replay_video')}
          </button>
        </div>
      )}

      {/* Sound toggle + video progress — shown while playing */}
      {videoPlaying && !allEnded && (
        <div className="hero-video-controls">
          <button
            className="video-ctrl-btn"
            onClick={toggleMute}
            data-testid="hero-mute-toggle-button"
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            {muted ? t('hero.sound_on') : t('hero.sound_off')}
          </button>
          {/* Video progress dots */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
            {VIDEOS.map((_, i) => (
              <div key={i} style={{
                width: i === currentVideoIdx ? '24px' : '8px',
                height: '4px',
                borderRadius: '2px',
                background: i === currentVideoIdx
                  ? 'rgba(212,175,55,0.9)'
                  : i < currentVideoIdx
                  ? 'rgba(212,175,55,0.5)'
                  : 'rgba(255,255,255,0.2)',
                transition: 'all 0.4s ease',
              }} />
            ))}
          </div>
        </div>
      )}

      <div className="hero-scroll-hint" aria-hidden="true">
        <span>{t('hero.scroll')}</span>
        <div className="scroll-dot" />
      </div>
    </section>
  );
};
